import React, { useState, useEffect } from "react";
import { db, functions } from '../../firebase/FirebaseProvider';
import { collection, getDocs, query, where, orderBy, Timestamp } from 'firebase/firestore';
import { Event, EventType, User } from "../types/Types";
import { httpsCallable } from "@firebase/functions";
import { Card } from "react-bootstrap";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router";
import AuthCheck from "../auth/AuthCheck";
import PageTracking from "../auth/PageTracking";
import EventsCard from "./EventsCard";
import EventModal from "./EventModal";
import { logDataLoaded } from "../util/Util";

const Events: React.FC = () => {
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState<boolean>(true);
  const [currentUser, setCurrentUser] = useState<User>({} as User);
  const [events, setEvents] = useState<Array<Event>>([]);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  
  // Form fields
  const [eventTitle, setEventTitle] = useState<string>('');
  const [eventType, setEventType] = useState<EventType | ''>('');
  const [eventDescription, setEventDescription] = useState<string>('');
  const [scheduledAt, setScheduledAt] = useState<string>('');
  const [locationId, setLocationId] = useState<string>('');
  const [eventCompleted, setEventCompleted] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      if (!authUser) {
        navigate('/');
        return;
      }
      const userCollection = query(collection(db, 'users'), where('authId', '==', authUser.uid));
      const userCollectionSnap = await getDocs(userCollection);
      userCollectionSnap.forEach((userDoc) => {
        let userToSet: User = userDoc.data() as User;
        userToSet.id = userDoc.id;
        setCurrentUser(userToSet);
      });
      loadEvents();
    });

    return () => unsubscribe();
  }, [navigate]);

  const loadEvents = async () => {
    setLoading(true);
    try {
      const eventsCollection = query(
        collection(db, 'events'),
        orderBy('scheduledAt', 'asc')
      );
      const eventsSnap = await getDocs(eventsCollection);
      const eventsList: Array<Event> = [];
      eventsSnap.forEach((doc) => {
        const eventData = doc.data();
        eventsList.push({
          id: doc.id,
          userId: eventData.userId,
          createdAt: eventData.createdAt?.toDate(),
          scheduledAt: eventData.scheduledAt?.toDate() || null,
          eventTitle: eventData.eventTitle,
          eventType: eventData.eventType,
          eventDescription: eventData.eventDescription,
          locationId: eventData.locationId,
          eventCompleted: eventData.eventCompleted || false,
        } as Event);
      });
      logDataLoaded('events', eventsList.length);
      setEvents(eventsList);
    } catch (error) {
      console.error('Error loading events:', error);
    }
    setLoading(false);
  };

  const handleShowModal = (event?: Event) => {
    if (event) {
      setEditingEvent(event);
      setEventTitle(event.eventTitle);
      setEventType(event.eventType);
      setEventDescription(event.eventDescription || '');
      setScheduledAt(event.scheduledAt ? event.scheduledAt.toISOString().slice(0, 10) : '');
      setLocationId(event.locationId || '');
      setEventCompleted(event.eventCompleted || false);
    } else {
      setEditingEvent(null);
      setEventTitle('');
      setEventType('');
      setEventDescription('');
      setScheduledAt('');
      setLocationId('');
      setEventCompleted(false);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingEvent(null);
  };

  const handleSaveEvent = async () => {
    if (!eventTitle || !eventType) {
      alert('Please fill in required fields');
      return;
    }

    setSaving(true);
    try {
      const scheduledDate = scheduledAt ? Timestamp.fromDate(new Date(scheduledAt)) : null;
      
      if (editingEvent) {
        // Update existing event
        const updateEvent = httpsCallable(functions, 'updateEvent');
        await updateEvent({
          eventId: editingEvent.id,
          eventTitle,
          eventType,
          eventDescription: eventDescription || null,
          scheduledAt: scheduledDate || null,
          locationId: locationId || null,
          eventCompleted,
        });
      } else {
        // Create new event
        const createEvent = httpsCallable(functions, 'createEvent');
        await createEvent({
          eventTitle,
          eventType,
          eventDescription: eventDescription || null,
          scheduledAt: scheduledDate,
          locationId: locationId || null,
        });
      }
      
      handleCloseModal();
      loadEvents();
    } catch (error) {
      console.error('Error saving event:', error);
      alert('Failed to save event');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div>
        <AuthCheck />
        <PageTracking />
        <Card className="mb-3">
          <Card.Body>
            <div className={'loading'}>Loading...</div>
          </Card.Body>
        </Card>
      </div>
    );
  }

  // Filter events into active and completed
  const activeEvents = events
    .filter(event => !event.eventCompleted)
    .sort((a, b) => {
      // Events without scheduledAt go to the end
      if (!a.scheduledAt && !b.scheduledAt) return 0;
      if (!a.scheduledAt) return 1;
      if (!b.scheduledAt) return -1;
      // Otherwise sort by date (already sorted from query, but explicit here)
      return new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime();
    });
  const pastEvents = events
    .filter(event => event.eventCompleted)
    .sort((a, b) => {
      // Events without scheduledAt go to the end
      if (!a.scheduledAt && !b.scheduledAt) return 0;
      if (!a.scheduledAt) return 1;
      if (!b.scheduledAt) return -1;
      // Reverse past event order so the most recent past event appears first
      return new Date(b.scheduledAt).getTime() - new Date(a.scheduledAt).getTime();
    });

  return (
    <div>
      <AuthCheck />
      <PageTracking />

      {/* Active Events Card */}
      <EventsCard
        title="Upcoming Events"
        events={activeEvents}
        currentUser={currentUser}
        onEventClick={handleShowModal}
        onNewEvent={() => handleShowModal()}
        emptyMessage="No events scheduled"
      />

      {/* Past Events Card */}
      {pastEvents.length > 0 && <EventsCard
        title="Past Events"
        events={pastEvents}
        currentUser={currentUser}
        onEventClick={handleShowModal}
        emptyMessage="No past events"
      />}

      {/* Event Modal */}
      <EventModal
        show={showModal}
        editingEvent={editingEvent}
        eventTitle={eventTitle}
        eventType={eventType}
        eventDescription={eventDescription}
        scheduledAt={scheduledAt}
        eventCompleted={eventCompleted}
        saving={saving}
        onHide={handleCloseModal}
        onSave={handleSaveEvent}
        onTitleChange={setEventTitle}
        onTypeChange={setEventType}
        onDescriptionChange={setEventDescription}
        onScheduledAtChange={setScheduledAt}
        onCompletedChange={setEventCompleted}
      />
    </div>
  );
};

export default Events;
